/**
 * <FileChip> — the client-side render of a chat file reference (workspace-file-
 * tags). Given an `@tag` body (or a bare machine-name token), it resolves the file
 * via `useWorkspaceAliasMap` and renders the friendly label as a compact, clickable
 * chip that opens the file in the WorkspaceFilePane. A miss renders plain text
 * (never any raw tag syntax) — see `resolveChipRender`.
 *
 * The lookup is reactive (the hook derives from the tree query), so renaming a
 * file relabels its chips across already-rendered history for free.
 */

import { FileText } from "lucide-react";
import { useWorkspaceAliasMap } from "@/hooks/useWorkspaceAliasMap";
import { useWorkspaceFileStore } from "@/stores/useWorkspaceFileStore";
import { resolveChipRender } from "./file-chip-render";

interface FileChipProps {
  /** The normalized tag payload (case-rel path or basename), or a bare machine name. */
  payload: string;
  /** Whether the reference came from an `@tag` or a bare machine name. */
  source: "tag" | "bare";
}

export function FileChip({ payload, source }: FileChipProps) {
  const map = useWorkspaceAliasMap();
  const openFile = useWorkspaceFileStore((s) => s.openFile);
  const render = resolveChipRender(payload, source, map);

  if (render.kind === "text") {
    // Miss — render the bare name as plain text (today's status quo).
    return <>{render.text}</>;
  }

  return (
    <button
      type="button"
      data-testid="chat-file-chip"
      title={render.tooltip || undefined}
      onClick={(e) => openFile({ path: render.path, title: render.tooltip || render.label }, e.currentTarget)}
      className="mx-0.5 inline-flex max-w-full items-center gap-1 rounded border border-cream-300 bg-cream-100/40 px-1.5 py-0.5 align-baseline text-caption text-coffee-700 transition-colors hover:bg-cream-200/60 dark:border-coffee-700 dark:bg-coffee-900/40 dark:text-cream-300 dark:hover:bg-coffee-800/60"
    >
      <FileText size={12} className="shrink-0 opacity-70" />
      <span className="truncate">{render.label}</span>
    </button>
  );
}
